import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Chip, IconButton, InputAdornment, Pagination, Paper, Skeleton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Replay as UnexcludeIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';
import { useAdminTheme } from '../../admin-theme';
import { useScrollToListTop } from '../../hooks/useScrollToListTop';
import { darkTableSx, selectedChipProps } from './ui';
import StagedItemEditDialog, { StagedEditable } from './StagedItemEditDialog';
import NoWoocommerceDialog from './NoWoocommerceDialog';
import { stagingApiBase } from './stagingOrg';

// New items that will not become products, each with the reason it is here. Only an
// item a person excluded can be un-excluded, and only once Vend confirms it is still
// active (the server checks on the click): a type-excluded one comes back by
// including its type on Category Mappings, and an inactive one when Vaperite
// reactivates it in Vend — un-excluding either here would just be undone by the sync.

type Reason = 'type' | 'tag' | 'brand' | 'inactive' | 'person';

const REASONS: Reason[] = ['type', 'tag', 'brand', 'inactive', 'person'];
const REASON_LABELS: Record<Reason, string> = {
  type: 'Type excluded',
  tag: 'Tag excluded',
  // its Vend brand is Excluded on Brand Mappings (Jason, 29 Oct 2026)
  brand: 'Brand excluded',
  inactive: 'Inactive in Vend',
  person: 'Excluded by a person',
};
const PAGE_SIZE = 50;

export interface ExcludedItem {
  sku: string;
  name: string;
  type: string | null;
  imageUrl: string | null;
  basePrice: number | null;
  reason: Reason;
  note: string | null;
  excludedBy: string | null;
  excludedAt: string | null;
  canUnexclude: boolean;
  /** Why it cannot be un-excluded here (from the API), when it cannot. */
  lockedReason: string | null;
}

const formatPrice = (v: number | null) => (v == null ? '-' : `R${Number(v).toFixed(2)}`);
const formatDate = (iso: string | null) => (iso
  ? new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
  : '-');

/** What the reason chip says for a row: the type's name, or who excluded it. */
function reasonText(row: ExcludedItem): string {
  if (row.reason === 'type') return row.type ? `Type excluded: ${row.type}` : 'Type excluded';
  if (row.reason === 'tag') return 'Tag excluded';
  if (row.reason === 'brand') return 'Brand excluded';
  if (row.reason === 'inactive') return 'Inactive in Vend';
  return row.excludedBy ? `Excluded by ${row.excludedBy}` : 'Excluded by a person';
}

export default function ExcludedItemsTab({ onChanged }: { onChanged?: () => void }) {
  const { colors } = useAdminTheme();
  const notify = useNotify();
  const [items, setItems] = useState<ExcludedItem[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<Reason, number> | null>(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState<Reason | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const listTopRef = useScrollToListTop(page);
  const [busySku, setBusySku] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<StagedEditable | null>(null);
  // Quick filter/page changes overlap: only the latest request may fill the table.
  const requestSeq = useRef(0);
  // Un-exclude held by the server: no woocommerce tag in Vend — the person decides.
  const [noWooRow, setNoWooRow] = useState<ExcludedItem | null>(null);
  const [noWooBusy, setNoWooBusy] = useState(false);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/excluded`, {
        params: { reason: reason ?? undefined, search: search || undefined, page, limit: PAGE_SIZE },
      });
      if (seq !== requestSeq.current) return;
      setItems(data.items || []);
      setTotal(data.total ?? 0);
      setCounts(data.counts ?? null);
    } catch (err: any) {
      if (seq !== requestSeq.current) return;
      notify(`Failed to load excluded products: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [reason, search, page, notify]);

  useEffect(() => { load(); }, [load]);

  // Debounce typing into the search box.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const toggleReason = (r: Reason) => {
    setReason((prev) => (prev === r ? null : r));
    setPage(1);
  };

  const handleUnexclude = async (row: ExcludedItem, allowNoWoocommerce = false) => {
    setBusySku(row.sku);
    try {
      const { data } = await authInstance.post(`${stagingApiBase()}/staging/excluded/unexclude`, {
        skus: [row.sku], ...(allowNoWoocommerce ? { allowNoWoocommerce } : {}),
      });
      if ((data?.noWoocommerce ?? []).some((r: { sku: string }) => r.sku === row.sku)) {
        setNoWooRow(row);
        return;
      }
      // Vend said no (inactive, deleted, gone, or could not be checked): it stays here.
      const refusal = (data?.refused ?? []).find((r: { sku: string }) => r.sku === row.sku);
      if (refusal) {
        notify(`${row.sku} stays excluded: ${refusal.reason}`, { type: 'warning' });
        return;
      }
      if ((data?.pending ?? 0) + (data?.flagged ?? 0) === 0) {
        notify(`${row.sku} could not be un-excluded — it may have changed. Refreshing.`, { type: 'warning' });
        load();
        return;
      }
      notify(
        data.flagged > 0
          ? `${row.sku} un-excluded — it is missing values, so it went to Flagged Items`
          : `${row.sku} un-excluded — back in Pending Review`,
        { type: 'success' },
      );
      // Drop the row rather than refetching the page under the reviewer's cursor.
      setItems((prev) => prev.filter((i) => i.sku !== row.sku));
      setTotal((t) => Math.max(0, t - 1));
      setCounts((c) => (c ? { ...c, person: Math.max(0, c.person - 1) } : c));
      onChanged?.();
    } catch (err: any) {
      notify(`Failed to un-exclude: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setBusySku(null);
    }
  };

  // The dialog PATCHes every field, so load the full staged row first (as Flagged Items does).
  const handleOpenEdit = async (row: ExcludedItem) => {
    setBusySku(row.sku);
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/items/${row.sku}`);
      const item = data.item;
      setEditItem({
        sku: item.sku,
        type: item.type ?? 'new_item',
        name: item.suggestedName ?? row.name,
        description: item.suggestedDescription ?? null,
        basePrice: item.basePrice ?? null,
        category: item.categories?.[0]?.payloadCategoryName ?? null,
        tags: item.webhookTags ?? null,
        brand: item.canonicalItemData?.brand ?? null,
        gtin: item.gtin ?? null,
        imageUrl: item.suggestedImageUrl ?? null,
        sellInApp: item.sellInApp !== false,
        isRestricted: item.isRestricted === true,
        sellInAppOverridden: item.sellInAppOverridden === true,
      });
    } catch (err: any) {
      notify(`Failed to load ${row.sku}: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setBusySku(null);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Paper ref={listTopRef} sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}` }}>
        <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
          Excluded Products
        </Typography>
        <Typography variant="caption" sx={{ color: colors.textMuted }}>
          New items that will not become products, and why. Only an item a person excluded can be un-excluded here.
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mt: 2 }}>
          <TextField
            size="small"
            placeholder="Search SKU or name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 260 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
              },
            }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {REASONS.map((r) => {
              const active = reason === r;
              return (
                <Chip
                  key={r}
                  label={`${REASON_LABELS[r]} (${counts?.[r] ?? 0})`}
                  onClick={() => toggleReason(r)}
                  color={active ? 'warning' : 'default'}
                  variant={active ? 'filled' : 'outlined'}
                  {...selectedChipProps(active)}
                  size="small"
                />
              );
            })}
          </Stack>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ p: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={44} sx={{ bgcolor: colors.bgElevated, mb: 0.5 }} />
          ))}
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: colors.textMuted }}>
            No excluded products{reason || search ? ' match these filters' : ''}.
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={darkTableSx}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>SKU</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Excluded</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.sku}>
                  <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{row.sku}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    <Tooltip title={row.note ?? ''} disableHoverListener={!row.note}>
                      <Chip
                        label={reasonText(row)}
                        size="small"
                        variant="outlined"
                        color={row.reason === 'person' ? 'warning' : 'default'}
                      />
                    </Tooltip>
                    {row.reason === 'person' && row.note && (
                      <Typography variant="caption" sx={{ display: 'block', color: colors.textMuted, mt: 0.5 }}>
                        {row.note}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: `${colors.textSecondary} !important` }}>
                    {formatDate(row.excludedAt)}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatPrice(row.basePrice)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Edit"
                          disabled={busySku === row.sku}
                          onClick={() => handleOpenEdit(row)}
                          sx={{ color: colors.info }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={row.canUnexclude ? 'Un-exclude: back to review' : (row.lockedReason ?? '')}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Un-exclude"
                          disabled={!row.canUnexclude || busySku === row.sku}
                          onClick={() => handleUnexclude(row)}
                          sx={{ color: colors.success }}
                        >
                          <UnexcludeIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} size="small" />
        </Box>
      )}

      <NoWoocommerceDialog
        open={!!noWooRow}
        mode="unexclude"
        items={noWooRow ? [{ sku: noWooRow.sku, name: noWooRow.name }] : []}
        busy={noWooBusy}
        onSkip={() => setNoWooRow(null)}
        onConfirm={async () => {
          const row = noWooRow!;
          setNoWooBusy(true);
          await handleUnexclude(row, true);
          setNoWooBusy(false);
          setNoWooRow(null);
        }}
      />

      <StagedItemEditDialog
        open={!!editItem}
        item={editItem}
        onClose={() => setEditItem(null)}
        onSaved={() => load()}
      />
    </Paper>
  );
}
